/** Message contract between the tuner worklet and the main thread. */

/** Registered name of the processor. */
export const TUNER_PROCESSOR = 'tuner-processor'

/** Construction options passed to the worklet via `processorOptions`. */
export interface TunerProcessorOptions {
  /**
   * Decimated samples per posted block.
   *
   * Blocks are contiguous, not overlapping windows: the tracker is a set of
   * streaming filters and wants every sample exactly once. The size is a
   * latency knob and nothing else — smaller means the meters update sooner.
   */
  readonly blockSize: number
  /** Target rate of level messages per second. */
  readonly levelRateHz: number
  /**
   * Blocks to profile before reporting timing once. Needs to be large: the
   * only available clock ticks at 1 ms, so the estimate is statistical.
   */
  readonly timingBlocks: number
}

/** Broadband input level, emitted at roughly `levelRateHz`. */
export interface LevelMessage {
  readonly type: 'level'
  /** RMS over the interval since the previous level message, in [0, 1]. */
  readonly rms: number
  /** Largest absolute sample in that interval, for clipping detection. */
  readonly peak: number
  /** Worklet clock at the end of the interval, in seconds. */
  readonly time: number
}

/**
 * One contiguous block of the decimated stream.
 *
 * Not a window: consecutive blocks do not overlap and none is skipped. The
 * tracker carries its filter state across them, so a gap or a repeat would
 * show up as a phase jump, which is exactly what it measures pitch with.
 */
export interface BlockMessage {
  readonly type: 'block'
  /** Mono PCM samples, decimated, `blockSize` long. */
  readonly samples: Float32Array
  /**
   * Rate of `samples` in Hz, after decimation.
   *
   * Carried per block rather than assumed: the analysis path runs at a
   * different rate from the AudioContext, and every probe frequency is
   * meaningless if the two disagree.
   */
  readonly sampleRate: number
  /** Worklet clock at the end of the block, in seconds. */
  readonly time: number
}

/**
 * One-shot report on how long `process()` takes per render quantum.
 *
 * `AudioWorkletGlobalScope` exposes no high-resolution clock — `performance`
 * is simply absent in Chrome, and `currentTime` is frozen for the duration of
 * a block. The only usable clock is `Date.now()` at 1 ms resolution, which is
 * far coarser than a single block. Two things are therefore measured:
 *
 *   - `averageMs`, a statistical estimate: individual deltas are almost always
 *     0 and occasionally one whole tick, so the mean over thousands of blocks
 *     still converges on the true cost.
 *   - `slowBlocks`, the number of blocks whose execution spanned a tick
 *     boundary. This is NOT a count of blocks that took a full millisecond:
 *     for a block costing `c`, the expected rate is `c / clockResolutionMs`.
 *     It is useful as a consistency check on `averageMs` (the two are derived
 *     from the same ticks) and as a coarse outlier signal — a rate far above
 *     `averageMs / clockResolutionMs` means individual blocks are stalling.
 */
export interface TimingMessage {
  readonly type: 'timing'
  /** Blocks included in the measurement. */
  readonly blocks: number
  /** Samples per render quantum (128 in every current browser). */
  readonly blockSize: number
  /** Wall clock available per block at the current sample rate, in ms. */
  readonly budgetMs: number
  /** Estimated mean time spent inside `process()`, in ms. */
  readonly averageMs: number
  /** Blocks whose execution spanned a clock tick; see the note above. */
  readonly slowBlocks: number
  /** Resolution of the clock used, in ms. */
  readonly clockResolutionMs: number
  /** Estimated mean time as a percentage of the real-time budget. */
  readonly loadPercent: number
}

/** Every message the worklet can send. */
export type WorkletMessage = LevelMessage | BlockMessage | TimingMessage

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Runtime guard for level messages. */
export function isLevelMessage(value: unknown): value is LevelMessage {
  if (!isRecord(value)) return false
  return (
    value.type === 'level' &&
    typeof value.rms === 'number' &&
    typeof value.peak === 'number' &&
    typeof value.time === 'number'
  )
}

/** Runtime guard for sample blocks. */
export function isBlockMessage(value: unknown): value is BlockMessage {
  if (!isRecord(value)) return false
  return (
    value.type === 'block' &&
    value.samples instanceof Float32Array &&
    typeof value.sampleRate === 'number' &&
    value.sampleRate > 0 &&
    typeof value.time === 'number'
  )
}

/** Runtime guard for the timing report. */
export function isTimingMessage(value: unknown): value is TimingMessage {
  if (!isRecord(value)) return false
  return (
    value.type === 'timing' &&
    typeof value.blocks === 'number' &&
    typeof value.blockSize === 'number' &&
    typeof value.budgetMs === 'number' &&
    typeof value.averageMs === 'number' &&
    typeof value.slowBlocks === 'number' &&
    typeof value.clockResolutionMs === 'number' &&
    typeof value.loadPercent === 'number'
  )
}
